import { useRef } from "react";
import useFocoModal from "../../hooks/useFocoModal";
import DonutChart from "../widgets/DonutChart";
import Icon from "../Icon";
import { AG_TIPO_CLR } from "../../constants";
import { prodBand, fmtRatio, fmtHours } from "../../utils";

export default function HomePdfPreviewOverlay({agByTipo={}, agH=0, notionItems=[], label, currentUser, prod=null, onClose}) {
  const cajaRef = useRef(null);
  useFocoModal(cajaRef, true, onClose);
  const today = new Date().toLocaleDateString("es-ES",{day:"2-digit",month:"long",year:"numeric"});
  const notionDbs   = notionItems.filter(i=>i.item_type==="database");
  const notionPages = notionItems.filter(i=>i.item_type==="page");

  return (
    <div ref={cajaRef} role="dialog" aria-modal="true" aria-label="Vista previa del informe personal" style={{position:"fixed",inset:0,background:"var(--surface2)",zIndex:9999,display:"flex",flexDirection:"column",fontFamily:"Arial,Helvetica,sans-serif",fontSize:12,color:"#1e293b"}}>
      <style>{`@media print{.pdf-no-print{display:none!important}@page{margin:14mm 12mm}body{background:var(--surface)}}`}</style>

      {/* Barra superior */}
      <div className="pdf-no-print" style={{background:"var(--surface)",borderBottom:"1px solid #e2e8f0",padding:"10px 20px",display:"flex",alignItems:"center",gap:12,flexShrink:0,boxShadow:"0 1px 4px rgba(0,0,0,.06)"}}>
        <button onClick={onClose}
          style={{display:"flex",alignItems:"center",gap:6,padding:"7px 14px",borderRadius:8,border:"1px solid #e2e8f0",cursor:"pointer",fontSize:12,background:"var(--surface2)",color:"#475569",fontFamily:"inherit",fontWeight:600}}>
          ← Volver
        </button>
        <span style={{flex:1,fontWeight:700,fontSize:14,color:"#1e293b"}}>
          Vista previa del informe
          <span style={{fontWeight:400,color:"#64748b",marginLeft:8,fontSize:12}}>· Dashboard personal · {label}</span>
        </span>
        <button onClick={()=>window.print()}
          style={{display:"flex",alignItems:"center",gap:7,padding:"8px 20px",borderRadius:8,border:"none",cursor:"pointer",fontSize:13,fontWeight:700,background:"linear-gradient(135deg,#086C76,#087DAB)",color:"#fff",fontFamily:"inherit",boxShadow:"0 2px 8px #086C7640"}}>
          🖨️ Imprimir / Guardar PDF
        </button>
      </div>

      <div style={{flex:1,overflowY:"auto",padding:"28px 40px",background:"var(--surface2)"}}>
        <div style={{maxWidth:920,margin:"0 auto",background:"var(--surface)",borderRadius:12,boxShadow:"0 4px 24px rgba(0,0,0,.08)",overflow:"hidden"}}>

          {/* Cabecera */}
          <div style={{background:"linear-gradient(135deg,#0f172a 60%,#1e3a5f)",color:"#fff",padding:"28px 32px",position:"relative",overflow:"hidden"}}>
            <div style={{position:"absolute",right:-20,top:-20,width:160,height:160,borderRadius:"50%",background:"rgba(0,180,216,.12)"}}/>
            <div style={{position:"relative"}}>
              <div style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:".12em",color:"#7dd3fc",marginBottom:6}}>Informe Personal</div>
              <div style={{fontSize:24,fontWeight:800,marginBottom:4}}>Dashboard · {currentUser?.name}</div>
              <div style={{fontSize:11,color:"#94a3b8"}}>Período: {label} · Generado el {today}</div>
            </div>
          </div>

          <div style={{padding:"28px 32px"}}>

            {/* KPIs */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:28}}>
              {[
                ...(prod&&prod.index!==null ? [["Productividad", prod.index+"%", prodBand(prod.index).color]] : []),
                ["Horas cuaderno",  agH.toFixed(1)+"h",    "#8b5cf6"],
                ["Entradas",        Object.values(agByTipo).reduce((s,v)=>s+v,0)>0
                                      ? Object.keys(agByTipo).length+" tipos"
                                      : "0",                "#0b7d96"],
                ["Proyectos Notion",notionDbs.length,       "#22c55e"],
                ["Páginas Notion",  notionPages.length,     "#f59e0b"],
              ].map(([l,v,c])=>(
                <div key={l} style={{border:"1px solid #e2e8f0",borderRadius:10,padding:"14px 8px",textAlign:"center",background:"var(--surface2)"}}>
                  <div style={{fontSize:22,fontWeight:800,color:c,lineHeight:1}}>{v}</div>
                  <div style={{fontSize:10,color:"#64748b",textTransform:"uppercase",letterSpacing:".05em",marginTop:5}}>{l}</div>
                </div>
              ))}
            </div>

            {/* Cuaderno */}
            {Object.keys(agByTipo).length>0 ? (
              <>
                <div style={{fontWeight:700,fontSize:11,textTransform:"uppercase",letterSpacing:".07em",color:"#475569",borderBottom:"2px solid #e2e8f0",paddingBottom:6,marginBottom:12}}>
                  Cuaderno · {agH.toFixed(1)}h
                </div>
                {/* Reparto por productividad: da contexto al % de la cabecera */}
                {prod&&prod.index!==null&&(
                  <div style={{marginBottom:20}}>
                    <div style={{height:12,borderRadius:6,overflow:"hidden",display:"flex",background:"var(--surface2)",marginBottom:6}}>
                      {[["Alta",prod.alta,100],["Media",prod.media,50],["Baja",prod.baja,0]].map(([lb,h,pv])=>(
                        h>0 ? <div key={lb} style={{height:"100%",width:Math.round(h/(prod.horas||1)*100)+"%",background:prodBand(pv).color}}/> : null
                      ))}
                    </div>
                    <div style={{display:"flex",gap:14,flexWrap:"wrap",fontSize:10,color:"#475569"}}>
                      {[["Alta",prod.alta,100],["Media",prod.media,50],["Baja",prod.baja,0]].filter(([,h])=>h>0).map(([lb,h,pv])=>(
                        <span key={lb} style={{display:"flex",alignItems:"center",gap:4}}>
                          <span style={{width:7,height:7,borderRadius:2,background:prodBand(pv).color,display:"inline-block"}}/>
                          Productividad {lb.toLowerCase()} <b>{fmtHours(h)}</b>
                        </span>
                      ))}
                    </div>
                    <div style={{fontSize:10.5,color:"#475569",marginTop:7,lineHeight:1.5}}>
                      {prod.baja>0
                        ? <>Relación de dedicación <b>{fmtRatio(prod.ratio)}</b> — {fmtHours(prod.alta)} de alta productividad por cada {fmtHours(prod.baja)} de baja.</>
                        : <>{fmtHours(prod.alta)} de alta productividad y ninguna hora de baja.</>}
                      {prod.sinTipo>0 && <> {fmtHours(prod.sinTipo)} sin tipo quedan fuera del índice.</>}
                    </div>
                  </div>
                )}
                <div style={{display:"flex",gap:20,alignItems:"flex-start",marginBottom:24}}>
                  <DonutChart
                    data={Object.entries(agByTipo).map(([tp,h])=>({value:h,color:AG_TIPO_CLR[tp]||"#9ca3af"})).filter(d=>d.value>0)}
                    size={88} stroke={14}/>
                  <table style={{flex:1,borderCollapse:"collapse",fontSize:12}}>
                    <thead><tr style={{background:"var(--surface2)"}}>
                      {["Tipo","Horas","% del total"].map(h=>(
                        <th key={h} style={{padding:"8px 11px",textAlign:"left",fontWeight:700,fontSize:10,color:"#64748b",borderBottom:"2px solid #e2e8f0",textTransform:"uppercase",letterSpacing:".04em"}}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {Object.entries(agByTipo).sort((a,b)=>b[1]-a[1]).map(([tp,h],i)=>{
                        const col = AG_TIPO_CLR[tp]||"#9ca3af";
                        const pct = agH ? Math.round(h/agH*100) : 0;
                        return (
                          <tr key={tp} style={{background:i%2?"#fafafa":"#fff",borderBottom:"1px solid #f1f5f9"}}>
                            <td style={{padding:"8px 11px"}}>
                              <div style={{display:"flex",alignItems:"center",gap:7}}>
                                <span style={{width:9,height:9,borderRadius:"50%",background:col,flexShrink:0,display:"inline-block"}}/>
                                <span style={{fontWeight:600}}>{tp}</span>
                              </div>
                            </td>
                            <td style={{padding:"8px 11px",fontWeight:700,color:col}}>{h.toFixed(1)}h</td>
                            <td style={{padding:"8px 11px",color:"#64748b"}}>{pct}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <p style={{fontSize:12,color:"#94a3b8",fontStyle:"italic",marginBottom:24}}>Sin entradas en el cuaderno para este período.</p>
            )}

            {/* Proyectos Notion */}
            {notionDbs.length>0&&(
              <>
                <div style={{fontWeight:700,fontSize:11,textTransform:"uppercase",letterSpacing:".07em",color:"#475569",borderBottom:"2px solid #e2e8f0",paddingBottom:6,marginBottom:12}}>
                  Proyectos Notion ({notionDbs.length})
                </div>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,marginBottom:24}}>
                  <thead><tr style={{background:"var(--surface2)"}}>
                    {["Nombre","Tipo"].map(h=>(
                      <th key={h} style={{padding:"8px 11px",textAlign:"left",fontWeight:700,fontSize:10,color:"#64748b",borderBottom:"2px solid #e2e8f0",textTransform:"uppercase",letterSpacing:".04em"}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {notionDbs.map((p,i)=>(
                      <tr key={p.id||i} style={{background:i%2?"#fafafa":"#fff",borderBottom:"1px solid #f1f5f9"}}>
                        <td style={{padding:"8px 11px",fontWeight:600}}>{p.name||p.title||"—"}</td>
                        <td style={{padding:"8px 11px",color:"#64748b"}}>Base de datos</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            <div style={{textAlign:"center",fontSize:10,color:"#94a3b8",marginTop:16,paddingTop:12,borderTop:"1px solid #e2e8f0"}}>
              ALM Manager · Dashboard personal · {currentUser?.name} · {today}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
